import * as React from 'react';
import {Component, inject, deps, observer, StatefulComponent} from '../../lib/component';
import SearchFilters, {SearchFilterSection} from './filters';
import {ButtonPill} from '../button-pill';
import {classes, style} from 'typestyle';
import {important, percent, px, quote, url} from 'csx';
import {SVGIcon} from '../svg-icon';
import {colorSlate, colorWhite} from '../../theme/color';
import {horizontal, start, vertical, wrap} from 'csstips';
import {constantSearchFiltersWidth} from '../../theme/constant';
import {mediaTablet} from '../../theme/media';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import gql from 'graphql-tag';
import {Rightsholder, Tag, TagResults} from '../../types/graphql';
import maybe from '../../lib/maybe';
import {Mode} from '../../types/ui';

export type SearchSummaryProps = {
  query: string;
  onSuggestions?(suggestions: Tag[]): any;
};

type State = {
  artist: Tag | null;
  genre: Tag | null;
  theme: Tag | null;
  mood: Tag | null;
  rightsholder: Tag | null;
}

type PartialTagsFindResult = {
  matches: null | TagResults;
};

type SearchSummaryData = {
  artists: PartialTagsFindResult | null;
  genres: PartialTagsFindResult | null;
  themes: PartialTagsFindResult | null;
  moods: PartialTagsFindResult | null;
  rightsholders: PartialTagsFindResult | null;
};

type CombinedProps = SearchSummaryProps & InjectedGraphQLProps<SearchSummaryData>;


@inject(deps) @observer
class SearchSummary extends StatefulComponent<CombinedProps, State> {
  state: State = {
    genre: null,
    mood: null,
    theme: null,
    artist: null,
    rightsholder: null,
  };

  render() {
    const {query, data} = this.props;
    const {filterI18n, userState} = this.props.model.page.search;
    const sections = filterI18n.sections.map(this.renderSection).filter(Boolean);
    const {genre, mood, theme, rightsholder, artist} = this.state;

    const showSuggestions = !data.loading && !!query && !!userState.query;

    return (
      <div className={SearchSummary.styles.container}>
        {showSuggestions && genre && this.renderSuggestion(genre, Mode.GENRE)}
        {showSuggestions && mood && this.renderSuggestion(mood, Mode.MOOD)}
        {showSuggestions && theme && this.renderSuggestion(theme, Mode.THEME)}
        {showSuggestions && rightsholder && this.renderSuggestion(rightsholder, Mode.RIGHTSHOLDER)}
        {showSuggestions && artist && this.renderSuggestion(artist, Mode.ARTIST)}
        {sections.length > 0 && sections}
        <div className={SearchSummary.styles.spacer}/>
      </div>
    )
  }

  componentWillReceiveProps({data}: CombinedProps) {
    const artist = maybe<Tag>(
      () => data.artists!.matches!.results![0],
      null
    );

    const genre = maybe<Tag>(
      () => data.genres!.matches!.results![0],
      null
    );

    const mood = maybe<Tag>(
      () => data.moods!.matches!.results![0],
      null
    );

    const theme = maybe<Tag>(
      () => data.themes!.matches!.results![0],
      null
    );

    const rightsholder = maybe<Tag>(
      () => data.rightsholders!.matches!.results![0],
      null
    );

    this.setState({
      artist,
      genre,
      mood,
      theme,
      rightsholder,
    });

    if (this.props.onSuggestions)
      this.props.onSuggestions([genre, mood, theme, rightsholder, artist].filter(Boolean));
  }

  renderSection = (section: SearchFilterSection) => {
    if (!section.label) return null;

    return (
      <ButtonPill onClick={this.props.controller.page.search.resetSection.bind(null, section)} className={SearchSummary.styles.pill} muted={true} key={section.category}>
        <span className={SearchSummary.styles.label}>{section.category}</span>
        <span>{section.label}</span>
        <SVGIcon.Cross
          className={SearchSummary.styles.icon}
          size={8}
          color={colorSlate.toString()}
        />
      </ButtonPill>
    )
  };

  renderSuggestion = (tag: Tag, mode: Mode) => {
    if (!tag || this.isAlreadySet(tag, mode))
      return null;

    const label = this.getSuggestionLabel(tag, mode);

    return (
      <ButtonPill onClick={this.handleClickSuggestion.bind(this, tag, mode)} className={SearchSummary.styles.suggestionPill} key={mode}>
        <span className={SearchSummary.styles.suggestionLabel}>{label}</span>
        <SVGIcon.Cross
          className={classes(SearchSummary.styles.icon, SearchSummary.styles.iconAdd)}
          size={8}
          color={colorWhite.toString()}
        />
      </ButtonPill>
    )
  };

  isAlreadySet = (tag: Tag, mode: Mode) => {
    const {state} = this.props.model.page.search;

    return ['artist', 'rightsholder'].includes(mode)
      ? state.filters[mode] === tag.name
      : state.filters[mode] === tag.slug;
  };

  handleClickSuggestion = (tag: Tag, mode: Mode) => {
    const value = ['artist', 'rightsholder'].includes(mode)
      ? tag.name
      : tag.slug;

    this.props.controller.page.search.refineFilter(mode, value);
    this.props.controller.page.search.refineQuery('');
  };

  getSuggestionLabel = (tag: Tag, mode: Mode) => {
    switch (mode) {
      case 'rightsholder':
        return `Filter by label “${tag.name}”?`;

      case 'artist':
        return `Filter by artist “${tag.name}”?`;

      case 'mood':
        return `Filter on “${tag.name}” mood?`;

      case 'genre':
        return `Filter on “${tag.name}” genre?`;

      case 'theme':
        return `Filter on “${tag.name}” theme?`;
    }
  };

  static styles = {
    container: style(
      {
        ...horizontal,
        padding: '8px 8px 0',
        marginBottom: px(-8),
        overflowX: 'auto',
      },
      mediaTablet({
        marginRight: constantSearchFiltersWidth,
        ...wrap
      })),
    pill: style({
      ...vertical,
      ...start,
      position: 'relative',
      fontSize: px(12),
      marginRight: px(8),
      marginBottom: px(8),
      padding: '0.25em 2.4em 0.25em 1.8em'
    }),
    spacer: style({
      content: quote(''),
      width: px(1),
      marginRight: px(-1),
      flexShrink: 0,
    }),
    label: style({
      fontWeight: 300,
      color: colorSlate.toString()
    }),
    icon: style({
      position: important('absolute'),
      top: percent(50),
      right: px(12),
      marginTop: px(-4),
      zIndex: 1,
    }),
    iconAdd: style({
      transform: 'rotate(45deg)',
    }),
    suggestionPill: style({
      ...vertical,
      ...start,
      position: 'relative',
      fontSize: px(12),
      marginRight: px(8),
      marginBottom: px(8),
      padding: '0.25em 2.4em 0.25em 1.8em',
      height: '34px',
    }),
    suggestionLabel: style({
      color: important(colorWhite.toString())
    })
  }
}

export default SearchSummary;

export const ConnectedSearchSummary = graphql(gql`
  query (
  $query: String,
  ) {
    artists: find(query: $query) {
      matches: artists(pagination: {size: 1, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
    
    themes: find(query: $query) {
      matches: themes(pagination: {size: 1, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }

    genres: find(query: $query) {
      matches: genres(pagination: {size: 1, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }

    moods: find(query: $query) {
      matches: moods(pagination: {size: 1, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }

    rightsholders: find(query: $query) {
      matches: rightsholders(pagination: {size: 1, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: SearchSummaryProps) => ({
    variables,
    fetchPolicy: 'network-only'
  }),
})(SearchSummary);